import axios from "axios";
import {auth} from "@/firebase";
import {displayValue} from "@/composables/sentences";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;

axios.interceptors.request.use(async (config) => {
    const currentUser = auth.currentUser;
    if (currentUser) {
        const token = await currentUser.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

export const FIXED_GRID_SIZE = 6;

export async function getRandomSentence() {
    const url = `/universe/random?rowAmount=${FIXED_GRID_SIZE}&colAmount=${FIXED_GRID_SIZE}`;
    try {
        const response = await axios.get(url);
        return response.data?.sentences[0]?.value;

    } catch (err) {
        // ignore
    }
}

export async function getChallenges(searchTerm) {
    const url = `/explore?sentence=${searchTerm}&rowAmount=${FIXED_GRID_SIZE}&colAmount=${FIXED_GRID_SIZE}&graph=true`;

    try {
        const response = await axios.get(url);
        return response.data;
    } catch (err) {
        // ignore
    }
}

export async function getGraph(searchTerm) {
    const url = `/explore/${searchTerm}`;
    try {
        const response = await axios.get(url);
        return response.data;
    } catch (err) {
        // ignore
    }
}

export function getCSSVariable(varName, element = document.documentElement) {
    return getComputedStyle(element)
        .getPropertyValue(varName);
}

const px = "px";

export function updateCellSizeFromCols(colAmount, reservedHeightOverride = null) {
    const CONTENT_WIDTH = parseInt(getCSSVariable("--content-width")) || 1012;
    const SCREEN_CUT_OFF_WIDTH = 430;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const pagePadding = parseInt(getCSSVariable("--page-padding-x")) * 2;
    const HEADER_HEIGHT = 48;
    const SENTENCE_HEIGHT = 70; /* covers a two-line wrapped sentence */
    const MENU_HEIGHT = 115;
    const LANDSCAPE_GRID_WIDTH_FRACTION = 0.55;
    const portrait = isPortrait();
    // Landscape-mobile uses a side-by-side layout (sentence left, grid right),
    // so only the header is above the grid. Desktop is always vertical stacking.
    const landscapeMobile = !portrait && h <= 639;
    const RESERVED = reservedHeightOverride !== null
        ? reservedHeightOverride
        : (portrait || !landscapeMobile)
            ? HEADER_HEIGHT + SENTENCE_HEIGHT + MENU_HEIGHT
            : 0;

    const widthBased = portrait
        ? (w <= SCREEN_CUT_OFF_WIDTH || h <= SCREEN_CUT_OFF_WIDTH)
            ? Math.floor(w / colAmount)
            : Math.floor(Math.min(w - pagePadding, CONTENT_WIDTH - pagePadding) / colAmount)
        : landscapeMobile
            ? Math.floor(w * LANDSCAPE_GRID_WIDTH_FRACTION / colAmount)
            : Math.floor(Math.min(w - pagePadding, CONTENT_WIDTH - pagePadding) / colAmount);
    const heightBased = Math.floor((h - RESERVED) / colAmount);
    const value = Math.min(widthBased, heightBased);

    const half = String(value / 2);
    const full = String(value);
    setCSSVariable('--halfCellSize', half);
    setCSSVariable('--halfCellSizeInPx', half + px);
    setCSSVariable('--cellSize', full);
    setCSSVariable('--cellSizeInPx', full + px);
}

export function setCSSVariable(property, value) {
    document.documentElement.style.setProperty(property, String(value));
}

export async function updateBoard(searchTerm, segments, sentence, isValid, errorMessage, gameBoardClass, graph, reservedHeightOverride = null) {
    updateGridSize(reservedHeightOverride);
    let challenges = await getChallenges(searchTerm.value);
    if (challenges) {
        //convert this to props
        segments.value = challenges?.challengeUniverse?.segments;
        sentence.value = displayValue(challenges?.sentences[0]);

        graph.value = challenges?.graph;

        isValid.value = challenges?.valid;

        if (!isValid.value) {
            errorMessage.value = challenges?.errorMessage;
        }
        if (segments.value.length > 0) {
            gameBoardClass.value = "game-board";
        } else {
            gameBoardClass.value = "game-board-dark";
        }
    }
}

export function updateGridSize(reservedHeightOverride = null) {
    updateCellSizeFromCols(FIXED_GRID_SIZE, reservedHeightOverride);
    setCSSVariable('--rowAmount', FIXED_GRID_SIZE);
    setCSSVariable('--colAmount', FIXED_GRID_SIZE);
}

export function isPortrait() {
    return window.matchMedia("(orientation: portrait)").matches;
}

export function isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}


